import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Search,
  User,
  Calendar,
  Activity,
  Heart,
  Thermometer,
  Clock,
  CheckCircle,
  AlertCircle,
  Stethoscope,
  ClipboardList,
  ChevronRight,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getNoteTypeLabel = (noteType, t) => {
  return t(`noteTypes.${noteType}`) || noteType;
};

const VitalSignsDisplay = ({ vitals, t }) => {
  if (!vitals) return null;
  
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg">
      {vitals.blood_pressure_systolic && vitals.blood_pressure_diastolic && (
        <div className="flex items-center gap-2">
          <Heart className="h-4 w-4 text-red-500" />
          <div>
            <p className="text-xs text-gray-500">{t('vitals.bloodPressure')}</p>
            <p className="font-medium">{vitals.blood_pressure_systolic}/{vitals.blood_pressure_diastolic}</p>
          </div>
        </div>
      )}
      {vitals.heart_rate && (
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-pink-500" />
          <div>
            <p className="text-xs text-gray-500">{t('vitals.heartRate')}</p>
            <p className="font-medium">{vitals.heart_rate} bpm</p>
          </div>
        </div>
      )}
      {vitals.temperature && (
        <div className="flex items-center gap-2">
          <Thermometer className="h-4 w-4 text-orange-500" />
          <div>
            <p className="text-xs text-gray-500">{t('vitals.temperature')}</p>
            <p className="font-medium">{vitals.temperature}°C</p>
          </div>
        </div>
      )}
      {vitals.oxygen_saturation && (
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-blue-500" />
          <div>
            <p className="text-xs text-gray-500">{t('vitals.oxygenSaturation')}</p>
            <p className="font-medium">{vitals.oxygen_saturation}%</p>
          </div>
        </div>
      )}
    </div>
  );
};

const NoteCard = ({ note, onValidate, t }) => (
  <Card className="mb-4 hover:shadow-md transition-shadow">
    <CardContent className="p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={
            note.note_type === 'admission' ? 'border-green-500 text-green-700' :
            note.note_type === 'discharge' ? 'border-red-500 text-red-700' :
            'border-blue-500 text-blue-700'
          }>
            {getNoteTypeLabel(note.note_type, t)}
          </Badge>
          {note.is_validated ? (
            <Badge className="bg-green-100 text-green-800">
              <CheckCircle className="h-3 w-3 mr-1" />
              {t('status.validated')}
            </Badge>
          ) : note.is_draft ? (
            <Badge variant="secondary">{t('status.draft')}</Badge>
          ) : null}
        </div>
        <div className="text-sm text-gray-500 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {new Date(note.created_at).toLocaleDateString(undefined, {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      </div>
      
      <VitalSignsDisplay vitals={note.vital_signs} t={t} />
      
      <div className="space-y-3 mt-3">
        {note.subjective && (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">{t('notes.subjective')}</p>
            <p className="text-sm text-gray-700">{note.subjective}</p>
          </div>
        )}
        {note.objective && (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">{t('notes.objective')}</p>
            <p className="text-sm text-gray-700">{note.objective}</p>
          </div>
        )}
        {note.assessment && (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">{t('notes.assessment')}</p>
            <p className="text-sm text-gray-700">{note.assessment}</p>
          </div>
        )}
        {note.plan && (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">{t('notes.plan')}</p>
            <p className="text-sm text-gray-700">{note.plan}</p>
          </div>
        )}
      </div>
      
      <div className="flex items-center justify-between mt-4 pt-3 border-t">
        <p className="text-sm text-gray-500">
          <Stethoscope className="h-3 w-3 inline mr-1" />
          {note.specialist_name}
        </p>
        {!note.is_validated && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onValidate(note.id)}
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            {t('notes.validate')}
          </Button>
        )}
      </div>
    </CardContent>
  </Card>
);

const PatientHistoryView = ({ patientId, onClose, t }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteForm, setNoteForm] = useState({
    note_type: 'progress',
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
    vital_signs: {}
  });

  useEffect(() => {
    if (patientId) {
      fetchHistory();
    }
  }, [patientId]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clinical/patients/${patientId}/history`);
      setHistory(response.data);
    } catch (error) {
      toast.error(t('messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async () => {
    try {
      await axios.post(`${API_URL}/api/clinical/notes`, {
        patient_id: patientId,
        ...noteForm
      });
      toast.success(t('messages.noteCreated'));
      setShowNoteForm(false);
      setNoteForm({
        note_type: 'progress',
        subjective: '',
        objective: '',
        assessment: '',
        plan: '',
        vital_signs: {}
      });
      fetchHistory();
    } catch (error) {
      toast.error(t('messages.errorCreating'));
    }
  };

  const handleValidateNote = async (noteId) => {
    try {
      await axios.post(`${API_URL}/api/clinical/notes/${noteId}/validate`);
      toast.success(t('messages.noteValidated'));
      fetchHistory();
    } catch (error) {
      toast.error(t('messages.errorValidating'));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
    </div>;
  }

  const noteTypes = [
    'admission', 'progress', 'consultation', 'nursing', 
    'therapy', 'diagnostic', 'medication', 'discharge', 'other'
  ];

  return (
    <div className="space-y-6">
      {/* Patient Header */}
      {history?.patient && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-[#FDE8EA] flex items-center justify-center">
                  <User className="h-6 w-6 text-[#E63946]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">
                    {history.patient.first_name} {history.patient.last_name}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {history.patient.document_type}: {history.patient.document_number}
                  </p>
                </div>
              </div>
              <Button onClick={() => setShowNoteForm(true)} data-testid="new-clinical-note-btn">
                <Plus className="h-4 w-4 mr-2" />
                {t('notes.newNote')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Background Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('history.background')}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">{t('history.allergies')}</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {history?.allergies?.length > 0 ? (
                history.allergies.map((allergy, i) => (
                  <Badge key={i} variant="destructive" className="bg-red-100 text-red-800">
                    {allergy}
                  </Badge>
                ))
              ) : (
                <span className="text-sm text-gray-400">{t('history.noAllergies')}</span>
              )}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">{t('history.chronicConditions')}</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {history?.chronic_conditions?.length > 0 ? (
                history.chronic_conditions.map((condition, i) => (
                  <Badge key={i} variant="outline">{condition}</Badge>
                ))
              ) : (
                <span className="text-sm text-gray-400">{t('history.noConditions')}</span>
              )}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">{t('history.surgeries')}</p>
            <p className="text-sm mt-1">
              {history?.surgical_history?.join(', ') || t('history.noSurgeries')}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">{t('history.medications')}</p>
            <p className="text-sm mt-1">
              {history?.current_medications?.join(', ') || t('history.noMedications')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Clinical Notes Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            {t('notes.title')} ({history?.notes?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history?.notes?.length > 0 ? (
            history.notes.map(note => (
              <NoteCard 
                key={note.id} 
                note={note} 
                onValidate={handleValidateNote}
                t={t}
              />
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>{t('notes.noNotes')}</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowNoteForm(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('notes.createFirstNote')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Note Dialog */}
      <Dialog open={showNoteForm} onOpenChange={setShowNoteForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('form.title')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{t('form.noteType')}</Label>
              <Select 
                value={noteForm.note_type}
                onValueChange={(v) => setNoteForm({...noteForm, note_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {noteTypes.map((key) => (
                    <SelectItem key={key} value={key}>{t(`noteTypes.${key}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">{t('vitals.systolic')}</Label>
                <Input 
                  type="number" 
                  placeholder="120"
                  onChange={(e) => setNoteForm({
                    ...noteForm,
                    vital_signs: {...noteForm.vital_signs, blood_pressure_systolic: parseInt(e.target.value) || null}
                  })}
                />
              </div>
              <div>
                <Label className="text-xs">{t('vitals.diastolic')}</Label>
                <Input 
                  type="number" 
                  placeholder="80"
                  onChange={(e) => setNoteForm({
                    ...noteForm,
                    vital_signs: {...noteForm.vital_signs, blood_pressure_diastolic: parseInt(e.target.value) || null}
                  })}
                />
              </div>
              <div>
                <Label className="text-xs">{t('vitals.heartRateBpm')}</Label>
                <Input 
                  type="number" 
                  placeholder="72"
                  onChange={(e) => setNoteForm({
                    ...noteForm,
                    vital_signs: {...noteForm.vital_signs, heart_rate: parseInt(e.target.value) || null}
                  })}
                />
              </div>
              <div>
                <Label className="text-xs">{t('vitals.temperatureC')}</Label>
                <Input 
                  type="number" 
                  step="0.1"
                  placeholder="36.5"
                  onChange={(e) => setNoteForm({
                    ...noteForm,
                    vital_signs: {...noteForm.vital_signs, temperature: parseFloat(e.target.value) || null}
                  })}
                />
              </div>
            </div>

            <div>
              <Label>{t('notes.subjective')} - {t('notes.subjectiveDesc')}</Label>
              <Textarea 
                placeholder={t('notes.subjectivePlaceholder')}
                value={noteForm.subjective}
                onChange={(e) => setNoteForm({...noteForm, subjective: e.target.value})}
                rows={3}
              />
            </div>

            <div>
              <Label>{t('notes.objective')} - {t('notes.objectiveDesc')}</Label>
              <Textarea 
                placeholder={t('notes.objectivePlaceholder')}
                value={noteForm.objective}
                onChange={(e) => setNoteForm({...noteForm, objective: e.target.value})}
                rows={3}
              />
            </div>

            <div>
              <Label>{t('notes.assessment')} - {t('notes.assessmentDesc')}</Label>
              <Textarea 
                placeholder={t('notes.assessmentPlaceholder')}
                value={noteForm.assessment}
                onChange={(e) => setNoteForm({...noteForm, assessment: e.target.value})}
                rows={3}
              />
            </div>

            <div>
              <Label>{t('notes.plan')} - {t('notes.planDesc')}</Label>
              <Textarea 
                placeholder={t('notes.planPlaceholder')}
                value={noteForm.plan}
                onChange={(e) => setNoteForm({...noteForm, plan: e.target.value})}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoteForm(false)}>
              {t('form.cancel')}
            </Button>
            <Button onClick={handleCreateNote} data-testid="save-clinical-note-btn">
              {t('form.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const ClinicalPage = () => {
  const { t } = useTranslation('clinical');
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [recentNotes, setRecentNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const listRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    // Reset and refetch when search changes (with debounce effect)
    const timer = setTimeout(() => {
      resetAndFetchPatients();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [patientsRes, notesRes] = await Promise.all([
        axios.get(`${API_URL}/api/patients?limit=20&page=1`),
        axios.get(`${API_URL}/api/clinical/notes?limit=10`)
      ]);
      
      const patientsData = Array.isArray(patientsRes.data) ? patientsRes.data : (patientsRes.data?.data || []);
      setPatients(patientsData);
      setHasMore(patientsData.length >= 20);
      
      const notesData = Array.isArray(notesRes.data) ? notesRes.data : (notesRes.data?.data || []);
      setRecentNotes(notesData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetAndFetchPatients = async () => {
    setPage(1);
    setHasMore(true);
    await fetchPatients(1, true);
  };

  const fetchPatients = async (pageNum = page, isReset = false) => {
    if (isReset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const params = new URLSearchParams({
        page: pageNum.toString(),
        limit: '20'
      });
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API_URL}/api/patients?${params}`);
      const newData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      
      if (isReset) {
        setPatients(newData);
      } else {
        setPatients(prev => [...prev, ...newData]);
      }
      
      // Check if there are more pages
      const totalPages = response.data?.pagination?.total_pages || (newData.length >= 20 ? pageNum + 1 : pageNum);
      setHasMore(pageNum < totalPages);
      setPage(pageNum);
    } catch (error) {
      console.error('Error fetching patients:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchPatients(page + 1, false);
    }
  }, [loadingMore, hasMore, page]);

  // Intersection Observer for infinite scroll
  const lastPatientRef = useCallback(node => {
    if (loading || loadingMore) return;
    
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMore();
      }
    }, {
      root: listRef.current,
      rootMargin: '100px',
      threshold: 0.1
    });
    
    if (node) observerRef.current.observe(node);
  }, [loading, loadingMore, hasMore, loadMore]);

  // Filter patients locally for instant feedback while typing
  const filteredPatients = patients.filter(p => 
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.document_number?.includes(searchTerm)
  );

  if (selectedPatient) {
    return (
      <div data-testid="clinical-page">
        <Button 
          variant="ghost" 
          onClick={() => setSelectedPatient(null)}
          className="mb-4"
        >
          {t('backToList')}
        </Button>
        <PatientHistoryView 
          patientId={selectedPatient} 
          onClose={() => setSelectedPatient(null)}
          t={t}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clinical-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
          <p className="text-gray-500 mt-1">{t('subtitle')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Patient List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">{t('selectPatient')}</CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input 
                placeholder={t('searchPlaceholder')}
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="patient-search-input"
              />
            </div>
          </CardHeader>
          <CardContent className="max-h-[500px] overflow-y-auto" ref={listRef}>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
              </div>
            ) : filteredPatients.length > 0 ? (
              <div className="space-y-2">
                {filteredPatients.map((patient, index) => {
                  const isLast = index === filteredPatients.length - 1;
                  return (
                    <div 
                      key={patient.id}
                      ref={isLast ? lastPatientRef : null}
                      className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors flex items-center justify-between"
                      onClick={() => setSelectedPatient(patient.id)}
                      data-testid={`patient-item-${patient.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-[#FDE8EA] flex items-center justify-center">
                          <User className="h-5 w-5 text-[#E63946]" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{patient.first_name} {patient.last_name}</p>
                          <p className="text-xs text-gray-500">{patient.document_type}: {patient.document_number}</p>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </div>
                  );
                })}
                {loadingMore && (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-[#E63946]" />
                  </div>
                )}
                {!hasMore && filteredPatients.length >= 20 && (
                  <p className="text-center text-sm text-gray-400 py-2">
                    {t('noMorePatients')}
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <User className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>{t('noPatients')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Notes */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">{t('recent.title')}</CardTitle>
            <CardDescription>{t('recent.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            {recentNotes.length > 0 ? (
              <div className="space-y-3">
                {recentNotes.map(note => (
                  <div 
                    key={note.id}
                    className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedPatient(note.patient_id)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{t(`noteTypes.${note.note_type}`)}</Badge>
                        {note.is_validated && (
                          <Badge className="bg-green-100 text-green-800">{t('status.validated')}</Badge>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(note.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {note.subjective || note.assessment || t('recent.noDescription')}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {t('recent.by')}: {note.specialist_name}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>{t('recent.noRecent')}</p>
                <p className="text-sm mt-1">{t('recent.selectPatientHint')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClinicalPage;
